import { useAppDispatch, useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import LoadingIndicator from 'components/common/LoadingIndicator';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  characterSettingActions,
  deleteCharacterSettings,
  fetchCharacterSettings,
  selectCharacterSettingLoading,
  selectCharacterSettings,
  selectCharacterSettingSelectedIds,
  setStatusCharacterSetting,
  updateCharacterSettings,
} from '../characterSettingSlice';
import CharacterSettingDetailModal from '../components/Detail/CharacterSettingDetailModal';
import './CharacterSettingPage.scss';

function CharacterSettingPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);
  const characters = useAppSelector(selectCharacterSettings);
  const loading = useAppSelector(selectCharacterSettingLoading);
  const selectedIds = useAppSelector(selectCharacterSettingSelectedIds);

  // state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchCharacterSettings());

    // Cleanup: reset selected IDs when leaving the page
    return () => {
      dispatch(characterSettingActions.clearSelection());
    };
  }, [dispatch]);

  const selectedSingleId = useMemo(() => (selectedIds.length === 1 ? selectedIds[0] : null), [selectedIds]);

  const handleAdd = () => {
    setEditingCharacter(null);
    setShowDetailModal(true);
  };

  const handleDetail = () => {
    if (!selectedSingleId) return;
    const character = characters.find((c) => c.id === selectedSingleId);
    if (character) {
      setEditingCharacter(character);
      setShowDetailModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setEditingCharacter(null);
  };

  const handleSaveCharacter = async () => {
    // Modal already handles API calls (create/update)
    // Just fetch updated data and close modal
    await dispatch(fetchCharacterSettings());
    handleCloseModal();
  };

  const handleDelete = () => {
    if (!selectedIds.length) return;

    // Count active characters (not deleted)
    const activeCharacters = characters.filter((c) => !c.delete_flg);
    const activeCount = activeCharacters.length;

    // Check if any selected character has status = true (ON)
    const anyDefaultCharacterSelected = selectedIds.some((id) => {
      const character = characters.find((char) => char.id === id);
      return character?.status;
    });

    // Determine which message to show
    let deleteConfirmMessage = '';
    if (selectedIds.length === activeCount) {
      // Deleting ALL active characters
      deleteConfirmMessage = t('characterSettingList.delete_confirm_all');
    } else if (anyDefaultCharacterSelected) {
      // Deleting characters including ones with status = true (ON)
      deleteConfirmMessage = t('characterSettingList.delete_confirm_on');
    } else {
      // Deleting only characters with status = false (OFF)
      deleteConfirmMessage = t('characterSettingList.delete_confirm_off');
    }

    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={deleteConfirmMessage}
        onConfirm={() => {
          dispatch(deleteCharacterSettings(selectedIds)).then(() => {
            // Fetch updated data
            dispatch(fetchCharacterSettings()).then(() => {
              showGlobalModal(() => (
                <AlertModal title={t('App.info')} message={t('characterSettingList.delete_success')} />
              ));
            });
          });
        }}
      />
    ));
  };

  const handleDefaultToggle = (id: number) => {
    dispatch(setStatusCharacterSetting(id)).then(() => {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('characterSettingList.default_update_success')} />
      ));
    });
  };

  const handleRestore = async (item: any) => {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('characterSettingList.restore_confirm')}
        onConfirm={async () => {
          const updatedData = characters.map((dataItem) => {
            if (dataItem.id === item.id) {
              return { ...dataItem, delete_flg: false };
            }
            return dataItem;
          });

          try {
            await dispatch(updateCharacterSettings(updatedData));
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={t('characterSettingList.restore_success')} />
            ));
          } catch (error) {
            console.error('Error restoring character:', error);
          }
        }}
      />
    ));
  };

  function renderCharacterRow(item: any) {
    const rowStyle = {
      backgroundColor: item.delete_flg ? 'rgb(242, 242, 242)' : '#fff', // Gray background for deleted items
    };

    return (
      <tr key={item.id} className="align-middle text-center">
        <td style={rowStyle}>
          {/* Show RESTORE button for deleted items */}
          {item.delete_flg && (
            <Button className="btn--orange" onClick={() => handleRestore(item)}>
              {t('characterSettingList.restore')}
            </Button>
          )}

          {/* Show CHECKBOX for active items */}
          {!item.delete_flg && (
            <Form.Check
              aria-label="option"
              id={item.id.toString()}
              checked={selectedIds.includes(item.id)}
              onChange={() => dispatch(characterSettingActions.toggleSelect(item.id))}
              style={{ fontSize: '1.5rem' }}
            />
          )}
        </td>
        <td style={rowStyle}>{item.name}</td>
        <td style={rowStyle}>
          <div className="d-flex gap-3 align-items-center justify-content-center">
            <div>
              {item.image_1 ? (
                <img
                  src={process.env.REACT_APP_IMAGE_URL + item.image_1}
                  alt={item.name}
                  style={{ maxWidth: '60px' }}
                />
              ) : null}
            </div>
          </div>
        </td>
        <td style={rowStyle}>
          <div className="d-flex gap-3 align-items-center justify-content-center">
            <div>
              {item.image_2 ? (
                <img
                  src={process.env.REACT_APP_IMAGE_URL + item.image_2}
                  alt={item.name}
                  style={{ maxWidth: '60px' }}
                />
              ) : null}
            </div>
          </div>
        </td>
        <td style={rowStyle}>{item.idle_time ?? '-'}</td>
        <td style={rowStyle}>
          <Form.Check
            disabled={item.delete_flg}
            type="switch"
            id={`default-${item.id}`}
            checked={item.status}
            onChange={() => handleDefaultToggle(item.id)}
            className="custom-character-default-switch"
            style={{ color: '#38D145', fontSize: '25px' }}
          />
        </td>
      </tr>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader onAction={() => history.push('/')} />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <div className="page-content d-flex flex-column">
            <div className="d-flex justify-content-between mb-3">
              <FeatureTitleBar title={t('characterSettingList.title')}></FeatureTitleBar>
            </div>
            <div className="mb-2 d-flex justify-content-end">
              <Button variant="success" onClick={handleAdd} className="btn--dark-green me-4">
                {t('characterSettingList.add')}
              </Button>
              <Button variant="primary" onClick={handleDetail} disabled={!selectedSingleId} className="me-4">
                {t('characterSettingList.detail')}
              </Button>
              <Button className="btn--red" onClick={handleDelete} disabled={!selectedIds.length}>
                {t('characterSettingList.delete')}
              </Button>
            </div>

            <div className="sticky-table flex-grow-1 flex-shrink-1">
              <Table hover>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ width: '5%' }}>
                      {t('characterSettingList.table.select')}
                    </th>
                    <th rowSpan={2} style={{ width: '40%' }}>
                      {t('characterSettingList.table.character_name')}
                    </th>
                    <th colSpan={2} style={{ width: '30%' }}>
                      {t('characterSettingList.table.image')}
                    </th>
                    <th rowSpan={2} style={{ width: '16%' }}>
                      {t('characterSettingList.table.idle_time')}
                    </th>
                    <th rowSpan={2} style={{ width: '9%' }}>
                      {t('characterSettingList.table.default')}
                    </th>
                  </tr>
                  <tr>
                    <th>{t('characterSettingList.table.image_1')}</th>
                    <th>{t('characterSettingList.table.image_2')}</th>
                  </tr>
                </thead>
                <tbody>{characters.map((item) => renderCharacterRow(item))}</tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {loading && <LoadingIndicator />}

      <CharacterSettingDetailModal
        key={editingCharacter ? `edit-${editingCharacter.id}` : 'create'}
        show={showDetailModal}
        onClose={handleCloseModal}
        character={editingCharacter}
        onSave={handleSaveCharacter}
      />
    </>
  );
}

export default CharacterSettingPage;
